'use client';

import BranchGrid from '@/app/branch/components/branch-grid';
import FullWidthSection from '@/components/full-width-section';
import SearchSelect from '@/components/search-select';
import BranchFilter from '@/components/tag-filter';
import { FILTER_KEYWORD_FOR_ALL } from '@/data/branch/filter.data';
import { useRegionList } from '@/services/query/region.query';
import { useKeywordList, useStoreList } from '@/services/query/store.query';
import { RegionEntity } from '@/services/types/region.type';
import { KeywordEntity } from '@/services/types/store.type';
import Image from 'next/image';
import { useSearchParams } from 'next/navigation';
import { useEffect, useState } from 'react';
import MapSection from './components/map-section';

// 전체 선택을 위한 더미 지역 객체
const ALL_REGION: RegionEntity = {
  value: 'all',
  label: '전체',
  count: 0,
  districts: [],
};

export default function Client() {
  const searchParams = useSearchParams();

  const { data: keywordList } = useKeywordList();
  const keywords = keywordList?.data || [];

  const LIMIT = 9;

  // URL에서 초기값 가져오기
  const urlPage = searchParams.get('page');
  const urlKeywords = searchParams.get('keywords');
  const urlProvince = searchParams.get('province');
  const urlDistrict = searchParams.get('district');
  // SEO 랜딩 URL(/branch?region=시도 시군구 읍면동)의 지역 문자열.
  // 필터링에는 쓰지 않고, 해당 지역 좌표 기준 거리순 정렬(regionSort)에만 사용한다.
  const urlRegion = searchParams.get('region') || undefined;

  const [page, setPage] = useState(urlPage ? parseInt(urlPage) : 1);

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    updateURL({ page: newPage.toString() });
    window.scrollTo({ top: 0, behavior: 'instant' });
  };

  // 키워드 검색필터
  const [keywordsFilter, setKeywordsFilter] = useState<KeywordEntity[]>(() => {
    if (urlKeywords) {
      const keywordValues = urlKeywords.split(',');
      const selectedKeywords = keywords.filter((k) =>
        keywordValues.includes(k.value),
      );
      return selectedKeywords.length > 0
        ? selectedKeywords
        : [FILTER_KEYWORD_FOR_ALL];
    }
    return [FILTER_KEYWORD_FOR_ALL];
  });

  const handleFilterToggle = (filter: KeywordEntity) => {
    setKeywordsFilter((prev) => {
      let newFilters: KeywordEntity[];

      if (filter.value === FILTER_KEYWORD_FOR_ALL.value) {
        // 'all' 클릭 시 'all'만 남김
        newFilters = [filter];
      } else if (prev.some((f) => f.value === FILTER_KEYWORD_FOR_ALL.value)) {
        // 'all'이 선택되어 있으면 'all'을 빼고 filter만 남김
        newFilters = [filter];
      } else {
        // 이미 선택된 필터면 제거, 아니면 추가
        newFilters = prev.some((f) => f.value === filter.value)
          ? prev.filter((f) => f.value !== filter.value)
          : [...prev, filter];
      }

      return newFilters;
    });
    setPage(1);
  };

  // 키워드 필터 변경 시 URL 업데이트
  useEffect(() => {
    const keywordValues = keywordsFilter
      .filter((f) => f.value !== FILTER_KEYWORD_FOR_ALL.value)
      .map((f) => f.value);

    updateURL({
      keywords: keywordValues.length > 0 ? keywordValues.join(',') : undefined,
      page: '1',
    });
  }, [keywordsFilter]);

  useEffect(() => {
    if (keywordsFilter.length === 0) {
      setKeywordsFilter([FILTER_KEYWORD_FOR_ALL]);
    }
  }, [keywordsFilter]);

  // URL 업데이트 함수
  const updateURL = (params: Record<string, string | undefined>) => {
    const newSearchParams = new URLSearchParams(searchParams.toString());

    Object.entries(params).forEach(([key, value]) => {
      if (value === undefined || value === '') {
        newSearchParams.delete(key);
      } else {
        newSearchParams.set(key, value);
      }
    });

    const newURL = `${window.location.pathname}?${newSearchParams.toString()}`;
    window.history.replaceState({}, '', newURL);
  };

  // 시/도 검색필터
  const [provinceFilter, setProvinceFilter] = useState<RegionEntity>();
  const handleProvinceChange = (province: RegionEntity) => {
    setProvinceFilter(province);
    setDistrictFilter(undefined);
    setPage(1);
    setKeywordsFilter([FILTER_KEYWORD_FOR_ALL]);
  };

  // 시/도 변경 시 URL 업데이트
  useEffect(() => {
    if (provinceFilter) {
      updateURL({
        province:
          provinceFilter.value === 'all' ? undefined : provinceFilter.value,
        district: undefined,
        keywords: undefined,
        page: '1',
      });
    }
  }, [provinceFilter]);

  //  시/군/구 검색필터
  const [districtFilter, setDistrictFilter] = useState<RegionEntity>();
  const handleDistrictChange = (district: RegionEntity) => {
    setDistrictFilter(district);
    setPage(1);
    setKeywordsFilter([FILTER_KEYWORD_FOR_ALL]);
  };

  // 시/군/구 변경 시 URL 업데이트
  useEffect(() => {
    if (districtFilter) {
      updateURL({
        district: districtFilter.value,
        keywords: undefined,
        page: '1',
      });
    }
  }, [districtFilter]);

  const getApiKeywords = (filters: KeywordEntity[]) => {
    if (filters.some((f) => f.value === FILTER_KEYWORD_FOR_ALL.value)) {
      return [];
    }
    return filters.map((f) => f.value);
  };
  const { data: storeList } = useStoreList({
    page,
    limit: LIMIT,
    province: provinceFilter?.value === 'all' ? '' : provinceFilter?.value,
    region: districtFilter?.value,
    keywords: getApiKeywords(keywordsFilter),
    regionSort: urlRegion,
  });
  const branches = storeList?.data?.stores || [];
  const totalCount = storeList?.data?.totalCount || 0;

  const { data: regionList } = useRegionList();
  const siDoRegions = regionList?.data || [];

  // 전체 옵션을 포함한 시/도 목록
  const totalSiDoRegions = [ALL_REGION, ...siDoRegions];

  // URL의 province/district로 초기 필터 동기화 (직접 링크 또는 새로고침 시)
  useEffect(() => {
    if (siDoRegions.length === 0 || !urlProvince) return;
    const province = siDoRegions.find((r) => r.value === urlProvince);
    if (province) {
      setProvinceFilter(province);
      if (urlDistrict) {
        const district = province.districts?.find(
          (d) => d.value === urlDistrict,
        );
        if (district) setDistrictFilter(district);
      }
    }
  }, [siDoRegions, urlProvince, urlDistrict]);

  const totalKeywords: KeywordEntity[] = [
    FILTER_KEYWORD_FOR_ALL,
    ...keywords.map((k) => ({
      ...k,
      label: `#${k.label}`,
    })),
  ];

  const { data: mapStoreList } = useStoreList({
    page: 1,
    limit: 100,
    province: provinceFilter?.value === 'all' ? '' : provinceFilter?.value,
    region: districtFilter?.value,
    keywords: getApiKeywords(keywordsFilter),
    regionSort: urlRegion,
  });
  const mapBranches = mapStoreList?.data?.stores || [];

  // 전체 선택 여부 확인
  const isAllSelected = provinceFilter?.value === 'all';
  return (
    <>
      {/* 검색 컴포넌트 - 웹 */}
      <FullWidthSection className="desktop:block bg-bg-graylight relative mx-auto hidden h-[386px]">
        <div className="absolute top-0 left-0 w-full">
          <MapSection branches={mapBranches} />
        </div>
        <div className="web-w relative mx-auto">
          <div className="absolute top-0 left-0">
            <div className="flex items-center justify-center gap-3 pt-8 pr-0 pb-0 pl-9">
              <SearchSelect
                triggerPlaceholder={'시/도 선택'}
                selected={provinceFilter ? [provinceFilter] : []}
                onFilterChange={(filter) =>
                  handleProvinceChange(filter as RegionEntity)
                }
                list={totalSiDoRegions}
                triggerAddClass="!h-[66px] !w-[191px]"
              />
              <SearchSelect
                triggerPlaceholder={'시/군/구 선택'}
                selected={districtFilter ? [districtFilter] : []}
                onFilterChange={(filter) =>
                  handleDistrictChange(filter as RegionEntity)
                }
                list={siDoRegions
                  .filter((region) => region.value === provinceFilter?.value)
                  .flatMap((region) => region.districts ?? [])}
                triggerAddClass="!h-[66px] !w-[191px]"
                disabled={isAllSelected}
              />
            </div>
          </div>
        </div>
      </FullWidthSection>

      {/* 검색 컴포넌트 - 모바일 */}
      <FullWidthSection className="bg-bg-graylight desktop:hidden">
        <div className="flex items-center justify-center gap-3 px-7 py-6">
          <SearchSelect
            triggerPlaceholder={'시/도 선택'}
            selected={provinceFilter ? [provinceFilter] : []}
            onFilterChange={(filter) =>
              handleProvinceChange(filter as RegionEntity)
            }
            list={totalSiDoRegions}
            triggerAddClass="!h-[54px]"
          />
          <SearchSelect
            triggerPlaceholder={'시/군/구 선택'}
            selected={districtFilter ? [districtFilter] : []}
            onFilterChange={(filter) =>
              handleDistrictChange(filter as RegionEntity)
            }
            list={siDoRegions
              .filter((region) => region.value === provinceFilter?.value)
              .flatMap((region) => region.districts ?? [])}
            triggerAddClass="!h-[54px]"
            disabled={isAllSelected}
          />
        </div>
      </FullWidthSection>

      <div className="desktop:flex desktop:justify-center">
        <div className="desktop:web-w desktop:w-full">
          {/* 검색 결과 카운트 */}
          <div className="text-level-2 typo-20 desktop:typo-28 desktop:pb-7 desktop:pt-14.5 flex items-center pt-10.5 pb-4 font-semibold">
            <div className="relative size-6">
              <Image
                alt="map-pin"
                src={'/images/icons/icon_map_pin.svg'}
                fill
                className="mr-1"
              />
            </div>
            <span className="text-main-orange font-extrabold">
              {totalCount}개의 지점
            </span>
            이 검색되었어요.
          </div>
          <div className="desktop:pb-17 pb-12">
            <BranchFilter
              list={totalKeywords}
              selected={keywordsFilter || []}
              onFilterChange={(filter) =>
                handleFilterToggle(filter as KeywordEntity)
              }
            />
          </div>
          <BranchGrid
            branches={branches}
            pageSize={LIMIT}
            totalCount={totalCount}
            page={page}
            onPageChange={handlePageChange}
          />
        </div>
      </div>
    </>
  );
}
